import ApiClient from "./apiClient";
import TokenManager from "./tokenManager";
// import { API_ENDPOINTS } from "@/shared/constants/apiEndpoints";
import { API_ENDPOINTS } from "./endpoints";
import { ApiException } from "./axiosInstance";
import {
  mapMeResponseToUser,
  parseTokenResponse,
  UNSUPPORTED_ROLE_CODE,
  UNSUPPORTED_ROLE_MESSAGE,
} from "./authMappers";
import {
  LoginResponse,
  RefreshTokenResponse,
  ApiResponse,
  User,
} from "@/services/types/common.types";
import type {
  AuthTokens,
  LogoutRequest,
  RefreshTokenRequest,
} from "@/services/types/auth.types";

/**
 * Auth Service
 * Handles authentication-related API calls
 */

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  username?: string;
  password: string;
  name: string;
  schoolId: string;
  role?: string;
}

class AuthService {
  /**
   * Login user. Stores the returned tokens in TokenManager and returns them.
   * The backend returns a flat snake_case token object (no user); callers must
   * follow up with getCurrentUser() (GET api/v1/auth/me) to obtain the user.
   */
  async login(credentials: LoginCredentials): Promise<AuthTokens> {
    // ApiClient returns the raw response body (no envelope is added), so treat it as unknown
    const body: unknown = await ApiClient.post(
      API_ENDPOINTS.AUTH.LOGIN,
      { email: credentials.email, password: credentials.password },
      { skipAuth: true },
    );

    const tokens = parseTokenResponse(body);
    if (!tokens || !tokens.refreshToken) {
      throw new ApiException("Login failed: unexpected server response", 502);
    }

    TokenManager.setTokens(tokens);
    return tokens;
  }

  /**
   * Fetch the current user (role, name, email, ...) using the stored Bearer token.
   * Pass `skipRefresh` right after login to avoid a refresh attempt on a brand-new token.
   */
  async getCurrentUser(options?: {
    skipRefresh?: boolean;
    /** /me has no email: pass the login form / persisted email to keep it on the user */
    email?: string;
  }): Promise<User> {
    const body: unknown = await ApiClient.get(
      API_ENDPOINTS.AUTH.ME,
      undefined,
      options?.skipRefresh ? { skipRefresh: true } : undefined,
    );

    const user = mapMeResponseToUser(body, { email: options?.email });
    if (!user) {
      throw new ApiException("Could not read user profile", 502);
    }
    // A user without any role we understand cannot use the app: fail loudly (callers clear the session)
    if (user.roles.length === 0) {
      throw new ApiException(
        UNSUPPORTED_ROLE_MESSAGE,
        403,
        UNSUPPORTED_ROLE_CODE,
      );
    }
    return user;
  }

  /**
   * Register new user
   */
  async register(data: RegisterData): Promise<LoginResponse> {
    const response = await ApiClient.post<LoginResponse>(
      API_ENDPOINTS.AUTH.REGISTER,
      data,
      { skipAuth: true },
    );

    if (response.success && response.data) {
      // Handle response format: { tokens: {...}, user: {...} }
      const responseData = response.data;
      const tokens = responseData.tokens;
      const user = responseData.user;
      const { accessToken, refreshToken, expiresIn } = tokens;

      // Store tokens
      TokenManager.setAccessToken(accessToken, expiresIn);
      TokenManager.setRefreshToken(refreshToken);

      // Return in LoginResponse format
      return {
        tokens: {
          accessToken,
          refreshToken,
          expiresIn,
        },
        user,
      };
    }

    throw new Error(response.error || "Registration failed");
  }

  /**
   * Refresh access token
   */
  async refreshToken(): Promise<RefreshTokenResponse> {
    const refreshToken = TokenManager.getRefreshToken();

    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    // Request body is { refresh_token }; response may be snake_case flat or the old shapes
    const refreshBody: RefreshTokenRequest = { refresh_token: refreshToken };
    const body: unknown = await ApiClient.post(
      API_ENDPOINTS.AUTH.REFRESH_TOKEN,
      refreshBody,
      { skipAuth: true, skipRefresh: true },
    );

    const tokens = parseTokenResponse(body);
    if (!tokens) {
      throw new Error("Token refresh failed");
    }

    TokenManager.setTokens(tokens);

    return {
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      expiresIn: tokens.expiresIn,
      refreshExpiresIn: tokens.refreshExpiresIn,
    };
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      const refreshToken = TokenManager.getRefreshToken();

      // Call logout endpoint with { refresh_token } (snake_case, confirmed by backend)
      if (refreshToken) {
        const logoutBody: LogoutRequest = { refresh_token: refreshToken };
        await ApiClient.post(API_ENDPOINTS.AUTH.LOGOUT, logoutBody);
      }
    } catch (error) {
      // Even if logout fails on server, clear local tokens
      console.error("Logout API call failed:", error);
    } finally {
      // Always clear local tokens from localStorage
      TokenManager.clearTokens();
    }
  }

  /**
   * Forgot password
   */
  async forgotPassword(email: string): Promise<ApiResponse> {
    return ApiClient.post(
      API_ENDPOINTS.AUTH.FORGOT_PASSWORD,
      { email },
      {
        skipAuth: true,
      },
    );
  }

  /**
   * Reset password
   */
  async resetPassword(
    token: string,
    newPassword: string,
  ): Promise<ApiResponse> {
    return ApiClient.post(
      API_ENDPOINTS.AUTH.RESET_PASSWORD,
      { token, newPassword },
      { skipAuth: true },
    );
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return TokenManager.hasValidTokens();
  }
}

// Create and export singleton instance
const authService = new AuthService();
export default authService;
